import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type {
  CreateWorkOrderRequest,
  UpdateWorkOrderRequest,
  ResolveDisputeRequest,
  WorkOrder,
  WorkOrderListFilters,
} from '../types/service.types';

/**
 * Still absent from this client (later phases): Job await-approval
 * (recommendations), the sublet pair (send-to-sublet / sublet-returned), and
 * JobPart backorder / the supersession trio.
 */
export const workOrderApi = {
  list: (params?: WorkOrderListFilters) =>
    tenantApi.getPaginated<WorkOrder>('/work-orders', { params }),

  get: (id: string) => tenantApi.getOne<WorkOrder>(`/work-orders/${id}`),

  create: (data: CreateWorkOrderRequest) => tenantApi.postOne<WorkOrder>('/work-orders', data),

  /**
   * D7-01: the door's `UpdateWorkOrderRequest` marks the lifecycle field
   * `prohibited` server-side. This method passes the caller's `data` object
   * straight through — it never inspects or rewrites it, so it cannot be the
   * one that smuggles that key in.
   */
  update: (id: string, data: UpdateWorkOrderRequest) =>
    tenantApi.patchOne<WorkOrder>(`/work-orders/${id}`, data),

  // ---- The 8 WO verbs plus `invoice`, all postOne<WorkOrder> (RESEARCH Correction #5) ----

  confirm: (id: string) => tenantApi.postOne<WorkOrder>(`/work-orders/${id}/confirm`),
  unconfirm: (id: string) => tenantApi.postOne<WorkOrder>(`/work-orders/${id}/unconfirm`),
  checkIn: (id: string) => tenantApi.postOne<WorkOrder>(`/work-orders/${id}/check-in`),
  pause: (id: string, reason: string) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/pause`, { reason }),
  resume: (id: string) => tenantApi.postOne<WorkOrder>(`/work-orders/${id}/resume`),
  readyForCollection: (id: string) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/ready-for-collection`),
  cancel: (id: string, reason: string) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/cancel`, { reason }),
  voidWorkOrder: (id: string, reason?: string) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/void`, reason ? { reason } : undefined),
  /** Body omitted entirely when `taxCents` is undefined, so the door applies its own zero default. */
  invoice: (id: string, taxCents?: number) =>
    tenantApi.postOne<WorkOrder>(
      `/work-orders/${id}/invoice`,
      taxCents === undefined ? undefined : { tax_cents: taxCents },
    ),

  // ---- Phase 16 post-invoice verbs (D16-25) ----

  /** 201 — returns the NEW work order; the original stays as the reversed record. */
  reverseAndReissue: (id: string, reason: string) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/reverse-and-reissue`, { reason }),
  unlock: (id: string, reason: string) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/unlock`, { reason }),
  dispute: (id: string, reason: string) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/dispute`, { reason }),
  resolveDispute: (id: string, body: ResolveDisputeRequest) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/resolve-dispute`, body),
  reopen: (id: string, reason: string) =>
    tenantApi.postOne<WorkOrder>(`/work-orders/${id}/reopen`, { reason }),
  close: (id: string) => tenantApi.postOne<WorkOrder>(`/work-orders/${id}/close`),

  /**
   * These three build the literal, `/api`-prefixed document URL for
   * `window.open()` — a browser navigation, NOT an axios request. D9-06:
   * same-origin cookie auth plus the server's `inline` disposition means the
   * browser's own viewer handles both print and download; the document is
   * never fetched here as a blob.
   */
  jobSheetPdfUrl: (id: string) => `/api/work-orders/${id}/job-sheet.pdf`,
  invoicePdfUrl: (id: string) => `/api/work-orders/${id}/invoice.pdf`,
  /** D13-13 (#408, SPL-04) — the customer-safe invoice variant (plan 13-08). */
  customerInvoicePdfUrl: (id: string) => `/api/work-orders/${id}/customer-invoice.pdf`,
};
